import posthog from 'posthog-js';
import { FlowTypes } from '~/enums';
import { isRecoverySession } from '~/helpers/recoveryCampaign.helper';

export const CONTACT_DETAILS_EARLIER_FLAG =
  'AB_Web_LoanApplication_ContactDetailsEarlier_MoreRecoverableLeads_NL';
export const CONTACT_DETAILS_EARLIER_LANDING_URL_KEY =
  'Web_Loan_application_Contact_details_earlier_More_recoverable_leads_NL';

/**
 * Logged-in users already have their contact details on file, so they're left
 * out of the contact details earlier A/B test.
 */
export const isEligibleForContactDetailsEarlier = (
  flowType: FlowTypes
): boolean => flowType !== FlowTypes.Authenticated;

/**
 * The applicant's variant in the contact details earlier A/B test, or undefined
 * when they're not part of it: logged-in users, and recovery sessions, which are
 * shown the test variant but must not be exposed. The flag isn't read for them.
 */
export const getContactDetailsEarlierVariant = (
  flowType: FlowTypes,
  { shouldSendExposure }: { shouldSendExposure: boolean }
): string | undefined => {
  if (!isEligibleForContactDetailsEarlier(flowType) || isRecoverySession()) {
    return undefined;
  }

  const variant = posthog.getFeatureFlag(CONTACT_DETAILS_EARLIER_FLAG, {
    send_event: shouldSendExposure,
  });
  return typeof variant === 'string' ? variant : undefined;
};

/**
 * The variant an application was created in, read from the parameter that
 * postApplication adds to its landing URL. Undefined when there's no landing
 * URL yet, or it has no variant (logged in, recovery session, or older).
 */
export const getContactDetailsEarlierVariantFromLandingUrl = (
  landingUrl: string | undefined
): string | undefined => {
  if (!landingUrl) return undefined;

  try {
    const variant = new URL(landingUrl).searchParams.get(
      CONTACT_DETAILS_EARLIER_LANDING_URL_KEY
    );
    return variant ?? undefined;
  } catch {
    // Not a valid URL, so it can't tell us the variant
    return undefined;
  }
};
